import classNames from "classnames";
import React, { FunctionComponent, useEffect, useRef } from "react";
import useCustomizationContext from "../../../../hooks/useCustomizationContext";
import { Panel } from "../../../../states/customization";
import { ContentPage } from "../../../../types/customization/content";
import { TemplatePageDefinition } from "../../../../types/customization/template";
import { hasSelectionInPanel } from "../../../../utils/customization/configuration";
import {
  getCoverFilteredPages,
  getFrontCoverPage,
  getNoCoverDefinitions,
} from "../../../../utils/customization/cover";
import { isMobileSize } from "../../../../utils/customization/device";
import { findPageDefinition } from "../../../../utils/customization/finders";
import { canSelectPageDefinitionsForOnePage } from "../../../../utils/customization/pageDefinition";
import StartingDateChoice from "../../../molecules/customization/StartingDateChoice";
import SelectionModal from "../../selection/SelectionModal";
import CoverPanel from "../panels/CoverPanel";
import CustomizationContent from "../CustomizationContent";
import CustomizationHeader, {
  CustomizationOnQuit,
  CustomizationOnSave,
} from "../CustomizationHeader";
import CustomizationPreview from "../CustomizationPreview";
import PageListOrganization from "../PageListOrganization";
import PageEditionPanel from "../panels/PageEditionPanel";
import TitlePanel from "../panels/TitlePanel";
import PictureEditorView from "../PictureEditorView";
import TextEditor from "../TextEditor";
import "./style.scss";

interface Props {
  onQuit?: CustomizationOnQuit;
  onSave?: CustomizationOnSave;
}

const Customization: FunctionComponent<Props> = ({ onQuit, onSave }) => {
  const {
    configuration,
    content,
    dispatch,
    selection,
    template,
    ui: { previewOpened, editedPicture, editedText, gallery, organizePageList, panel },
  } = useCustomizationContext();

  const panelRef = useRef<HTMLDivElement>(null);

  let firstPage: ContentPage;
  let firstPageDefinition: false | TemplatePageDefinition;
  let defaultPanel = Panel.EDITPAGE;

  useEffect(() => {
    if (
      ((template.templateType === "calendar" && template.startingDate) ||
        (content.templateType === "calendar" && content.startingDate)) &&
      content.pages.length > 0
    ) {
      dispatch({ type: "setCalendarInitialContent" });
    }
  }, [content.pages]);

  if (template.templateType === "book" || template.templateType === "calendar") {
    const frontCover = getFrontCoverPage(content.pages, template.pageDefinitions);
    firstPage = frontCover || content.pages[0];
    firstPageDefinition = findPageDefinition(firstPage.pageDefinitionKey, template);
    defaultPanel = frontCover ? Panel.EDITCOVER : Panel.EDITPAGE;
  } else {
    const noCoverDefinitions = getNoCoverDefinitions(template.pageDefinitions);
    const noCoverPages = getCoverFilteredPages(content.pages, noCoverDefinitions);
    firstPage = noCoverPages[0];
    firstPageDefinition =
      noCoverPages.length > 0 && findPageDefinition(firstPage.pageDefinitionKey, template);
  }

  const firstPageEditableTexts =
    !!firstPageDefinition &&
    firstPageDefinition.elementDefinitions.filter(({ type }) => type === "editable-text");
  const hasFonts =
    firstPageEditableTexts &&
    firstPageEditableTexts.length > 0 &&
    template.fontDefinitions.length > 1;
  const hasLayoutMenu =
    firstPageDefinition &&
    canSelectPageDefinitionsForOnePage(template, content, firstPageDefinition);
  const hasColors = firstPageDefinition && firstPageDefinition.backgroundColorKeys.length > 1;

  useEffect(() => {
    if (
      !editedPicture &&
      !isMobileSize() &&
      !panel.key &&
      firstPage &&
      (panel.current === Panel.CLOSED || panel.current === defaultPanel)
    ) {
      const hasStyles = hasLayoutMenu || hasColors || hasFonts;
      if (hasSelectionInPanel(template) && (selection.photos.length > 0 || !hasStyles)) {
        dispatch({
          key: firstPage.key,
          openedTab: "photos",
          panel: defaultPanel,
          type: "setPanel",
        });
      } else if (hasStyles && panel.openedTab !== "photos") {
        dispatch({ key: firstPage.key, panel: defaultPanel, type: "setPanel" });
      }
    }
  }, [defaultPanel, dispatch, editedPicture, firstPage, hasColors, hasFonts, hasLayoutMenu, panel]);

  const closePanelHandler = () => {
    dispatch({
      panel: Panel.CLOSED,
      type: "setPanel",
    });
  };

  useEffect(() => {
    const clickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (panelRef.current && target && panelRef.current.contains(target) === false) {
        closePanelHandler();
        document.removeEventListener("click", clickOutside);
      }
    };
    if (isMobileSize() && panel.current !== Panel.CLOSED) {
      window.setTimeout(() => {
        document.addEventListener("click", clickOutside);
      }, 500);
    }

    return () => {
      document.removeEventListener("click", clickOutside);
    };
  }, [closePanelHandler, panel.current, panelRef]);

  useEffect(() => {
    dispatch({
      options: configuration?.options,
      type: "verifyAndReplacePageDefinitions",
    });
    dispatch({
      type: "undoRedo",
      way: "reinitialize",
    });
  }, [configuration?.options]);

  const displayCalendarStartDateChoice =
    template.templateType === "calendar" &&
    !template.startingDate &&
    content.templateType === "calendar" &&
    !content.startingDate;

  if (organizePageList && organizePageList.opened === true) {
    return (
      <div className="Customization">
        <PageListOrganization mode={organizePageList.mode} />
      </div>
    );
  }

  if (previewOpened === true) {
    return (
      <div className="Customization">
        <CustomizationPreview />
      </div>
    );
  }

  return (
    <div className="Customization">
      <div className="Customization-header">
        <CustomizationHeader onQuit={onQuit} onSave={onSave} />
      </div>
      <div
        className={classNames("Customization-main", {
          "Customization-main--panelOpened": panel.current !== Panel.CLOSED && isMobileSize(),
        })}
      >
        <div className="Customization-content">
          <CustomizationContent />
        </div>
        <div
          className={classNames("Customization-panel", {
            "Customization-panel--closed": panel.current === Panel.CLOSED,
            "Customization-panel--opened": panel.current !== Panel.CLOSED,
          })}
          ref={panelRef}
        >
          {panel.current === Panel.EDITPAGE && !!panel.key && (
            <PageEditionPanel
              elementDefinitionKey={panel.elementDefinitionKey}
              openTab={panel.openedTab}
              pageKey={panel.key}
            />
          )}
          {panel.current === Panel.EDITCOVER && !!panel.key && (
            <CoverPanel openTab={panel.openedTab} pageKey={panel.key} />
          )}
          {panel.current === Panel.EDITTITLE && <TitlePanel />}
        </div>
      </div>

      {!!editedPicture && <PictureEditorView editedPicture={editedPicture} />}
      {!!editedText && <TextEditor editedText={editedText} />}
      {!displayCalendarStartDateChoice && gallery.opened && (
        <SelectionModal
          fixedLayout={gallery.fixedLayout}
          pageKey={gallery.pageKey}
          undoKey={gallery.undoKey}
        />
      )}
      {displayCalendarStartDateChoice && (
        <StartingDateChoice
          onConfirm={(date: string) => dispatch({ type: "setCalendarInitialContent", date })}
        />
      )}
    </div>
  );
};

export default Customization;
